import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";

/**
 * Supported relative date range presets
 */
export type RelativeDatePreset =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_30_days"
  | "last_90_days"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "year_to_date"
  | "last_12_months";

/**
 * Default date preset used when initializing date filters
 */
export const DEFAULT_DATE_PRESET: RelativeDatePreset = "last_30_days";

/**
 * Human-readable labels for relative date presets
 */
export const RELATIVE_DATE_LABELS: Record<RelativeDatePreset, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  year_to_date: "Year to date",
  last_12_months: "Last 12 months",
};

// Module-level cache for computed date ranges (refreshed when day changes)
let cachedDate: string | null = null;
let cachedRanges: Partial<
  Record<RelativeDatePreset, { from: string; to: string }>
> = {};

/**
 * Compute actual date range from a relative date preset
 * Returns ISO date strings (YYYY-MM-DD) for from and to dates
 *
 * Results are cached at the module level for the current day to avoid
 * repeated date calculations when called multiple times (e.g., in filter lists).
 */
export function computeRelativeDateRange(preset: RelativeDatePreset): {
  from: string;
  to: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatDateToISOInternal(today);

  // Invalidate cache if the day has changed
  if (cachedDate !== todayStr) {
    cachedDate = todayStr;
    cachedRanges = {};
  }

  // Return cached result if available
  if (cachedRanges[preset]) {
    return cachedRanges[preset];
  }

  let from: Date;
  let to: Date;

  switch (preset) {
    case "today":
      from = today;
      to = today;
      break;

    case "yesterday": {
      const yesterday = subDays(today, 1);
      from = yesterday;
      to = yesterday;
      break;
    }

    case "last_7_days":
      from = subDays(today, 6);
      to = today;
      break;

    case "last_30_days":
      from = subDays(today, 29);
      to = today;
      break;

    case "last_90_days":
      from = subDays(today, 89);
      to = today;
      break;

    case "this_week":
      from = startOfWeek(today, { weekStartsOn: 1 });
      to = endOfWeek(today, { weekStartsOn: 1 });
      break;

    case "last_week": {
      const lastWeek = subWeeks(today, 1);
      from = startOfWeek(lastWeek, { weekStartsOn: 1 });
      to = endOfWeek(lastWeek, { weekStartsOn: 1 });
      break;
    }

    case "this_month":
      from = startOfMonth(today);
      to = endOfMonth(today);
      break;

    case "last_month": {
      const lastMonth = subMonths(today, 1);
      from = startOfMonth(lastMonth);
      to = endOfMonth(lastMonth);
      break;
    }

    case "this_quarter":
      from = startOfQuarter(today);
      to = endOfQuarter(today);
      break;

    case "last_quarter": {
      const lastQuarter = subQuarters(today, 1);
      from = startOfQuarter(lastQuarter);
      to = endOfQuarter(lastQuarter);
      break;
    }

    case "year_to_date":
      from = startOfYear(today);
      to = today;
      break;

    case "last_12_months":
      from = subYears(today, 1);
      to = today;
      break;

    default:
      from = today;
      to = today;
  }

  const result = {
    from: formatDateToISOInternal(from),
    to: formatDateToISOInternal(to),
  };

  // Cache the result for this preset
  cachedRanges[preset] = result;
  return result;
}

/**
 * Internal helper to format Date to ISO string (guaranteed non-undefined)
 */
function formatDateToISOInternal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * All available relative date presets.
 * Exported for reuse in components that need to iterate over presets.
 */
export const ALL_PRESETS: RelativeDatePreset[] = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "year_to_date",
  "last_12_months",
];

/**
 * Detect if a date range matches a known preset for today's date.
 * Returns the matching preset label or null if no match.
 *
 * Note: This loops through all presets and computes date ranges on each call.
 * If performance becomes an issue with many filters, consider caching today's
 * computed ranges at the module level (refreshed daily) or memoizing results.
 *
 * @param from - Start date in ISO format (YYYY-MM-DD)
 * @param to - End date in ISO format (YYYY-MM-DD), optional for single-date filters
 * @returns The human-readable preset label (e.g., "Today", "Last 30 days") or null
 */
export function detectPresetFromDates(
  from: string | undefined,
  to?: string | undefined,
): string | null {
  if (!from) return null;

  for (const preset of ALL_PRESETS) {
    const range = computeRelativeDateRange(preset);

    // For range filters (both from and to provided)
    if (to !== undefined) {
      if (from === range.from && to === range.to) {
        return RELATIVE_DATE_LABELS[preset];
      }
    } else {
      // For single-date filters (only from provided, e.g., GTE)
      if (from === range.from) {
        return RELATIVE_DATE_LABELS[preset];
      }
    }
  }

  return null;
}

/**
 * Format a Date object to ISO date string (YYYY-MM-DD)
 * Returns undefined if date is not valid
 */
export function formatDateToISO(date: Date | undefined): string | undefined {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Parse an ISO date string (YYYY-MM-DD) to a Date object
 * Returns undefined if the string is not valid
 */
export function parseISODate(dateString: string | undefined): Date | undefined {
  if (!dateString) return undefined;

  // Check if it's in ISO format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return undefined;
  }

  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  // Verify the date is valid
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  // Verify the date components match (to catch invalid dates like 2024-02-31)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
}

/**
 * Safely parse a date string to a Date object
 * Returns undefined if the string is invalid or produces an invalid Date
 * Handles various date string formats including ISO format (YYYY-MM-DD)
 */
export function safeParseDateString(
  dateString: string | undefined | null,
): Date | undefined {
  if (!dateString) return undefined;

  // First try to parse as ISO date format (YYYY-MM-DD)
  const isoDate = parseISODate(dateString);
  if (isoDate) return isoDate;

  // Try to parse using Date constructor as fallback
  try {
    const date = new Date(dateString);
    // Check if the date is valid
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  } catch {
    // Invalid date string
  }

  return undefined;
}
